<template>
    <div
        data-dialog="true"
        id="printQuoteDialog"
        class="flex flex-col relative w-[885px] print:w-full h-full bg-gray-tier-3"
    >
        <header class="sticky top-0 bg-gray-tier-2 border-b border-keyline-primary">
            <div class="w-full flex items-center justify-between py-2 px-6 print:px-0">
                <h2>
                    {{ $t("Quote #") }}
                    <span class="font-medium">{{ data }}</span>
                </h2>
                <span class="print:hidden flex items-center justify-end gap-2">
                    <AppButton
                        v-if="isEmployee()"
                        @click.prevent="
                            toggleModal(AuditModal, $event, { quote_id: data })
                        "
                        :text="$t('Audit')"
                        icon="table-rows-hollow"
                        :disabled="inert"
                    />
                    <AppButton
                        @click="toggleEmailQuoteDialog($event, {id:data})"
                        icon="icon-email-envelope"
                        text="Email"
                    />
                    <DialogCloseButton />
                </span>
            </div>
        </header>
        <div
            v-if="quote && Object.keys(quote).length > 0"
            class="overflow-y-auto mt-[49px] relative"
            :class="scrollClasses"
            @scroll="handleScroll"
        >
            <RecordHeader
                :record="quote"
                class="px-6 print:px-0 py-3 placeholder-gray-120 !fixed top-[51px] overflow-x-scroll w-[885px] whitespace-nowrap type-secondary border-b border-keyline-primary bg-gray-tier-2"
            />
            <InsightsQuoteCustomerInfo :quote="quote" :salesData="salesData" fromDialog="true" />
            <Divider v-if="!settings.is_sourcing_mode" />
            <QuoteRecord
                :quote="quote"
                :rows="rows"
                :columns="columns"
                :cards="cards"
                :quotesSelectable="false"
                :sort="{ id: 'asc' }"
                :insights="true"
                :notesColumns="notesColumns"
                :fromDialog="true"
                class="px-6 print:px-0 mb-24"
            />
        </div>
        <div
            v-else
            class="print:hidden absolute inset-0 flex items-center justify-center"
        >
            <LoadingSpinner :large="true" />
        </div>
    </div>
</template>
<script setup>
import { computed, inject, markRaw, onMounted, ref } from "vue";
import { useEmailQuoteDialog, useModalDialog, useScroll, useUser } from "@/Composables";
import axios from "axios";
import {
    AppButton,
    AuditModal,
    DialogCloseButton,
    Divider, InsightsQuoteCustomerInfo,
    LoadingSpinner,
    MoneyFormat,
    QuoteRecord,
    RecordHeader,
    QuoteLineDetail,
    DateFormat,
} from "@/Components";

const route = inject("route");
const { settings } = useUser();
const { scrollClasses, handleScroll } = useScroll();

const props = defineProps(["data"]);
const isLoading = ref(true);
const quote = ref();
const cards = ref({});
const salesData = ref({});
const { toggleEmailQuoteDialog } = useEmailQuoteDialog();
const { isEmployee } = useUser();
const { toggleModal } = useModalDialog();

onMounted(() => {
    axios
        .get(route("api.v1.private.insights.quotes.show", props.data), {
            params: {
                with: ["quote", "cards", "salesData"],
            },
        })
        .then((response) => {
            quote.value = response.data["quote"];
            quote.value.notes.data = quote.value.notes;
            cards.value = response.data["cards"] || {};
            salesData.value = response.data["salesData"] || {};
            isLoading.value = false;
        });
});

const columns = [
    {
        name: "#",
        path: "line_number",
    },
    {
        name: "Item #",
        sortPath: "item_id",
        component: markRaw(QuoteLineDetail),
        props: row => ({
            linkText: row.item_id,
            routeName: 'web.catalog.products.show',
            routeParams: row.slug,
            linkDescription: cards.value?.[row.item_id]?.display_short_description ?? (row.concat_description ?? ''),
            line: row,
            quote: quote.value,
            card: cards.value?.[row.item_id],
            showManufacturer: true,
        }),
        noWrap: false
    },
    {
        name: "Qty",
        path: "quantity_ordered",
    },
    {
        name: "GP",
        path: "gross_profit",
        alignRight: true,
        cellClass: 'text-right',

    },
    {
        name: "Cost",
        path: "cost",
        component: markRaw(MoneyFormat),
        alignRight: true,
        props: (row) => {
            return {
                amount: row.cost,
                alignRight: true,
                cellClass: 'type-tertiary',
            };
        },

    },
    {
        name: "Price",
        path: "price",
        component: markRaw(MoneyFormat),
        alignRight: true,
        props: (row) => {
            return {
                amount: row.price,
                alignRight: true,
            };
        },
    },
    {
        name: "Srch",
        path: "surcharge",
        component: markRaw(MoneyFormat),
        alignRight: true,
        props: (row) => {
            return {
                amount: row.surcharge,
                alignRight: true,
                cellClass: 'type-tertiary',
            };
        },
    },
    {
        name: "Ext.",
        path: "subtotal",
        component: markRaw(MoneyFormat),
        alignRight: true,
        cellClass: 'font-medium !pr-6',
        headClass: '!pr-6',
        props: (row) => {
            return {
                amount: row.subtotal,
                alignRight: true,
            };
        },
    },
];

const notesColumns = [
    {
        name: "Enter Date",
        path: "enter_date",
        component: markRaw(DateFormat),
        props: (row) => {
            return {
                date: row.enter_date,
                class: 'type-tertiary leading-normal',
            };
        },
        vAlign: 'top',
    },
    {
        name: "Entered By",
        path: "enter_by",
        cellClass: 'leading-normal',
        vAlign: 'top',
    },
    {
        name: "Note",
        path: "note",
        noWrap: true,
        cellClass: 'leading-normal',
        vAlign: 'top',
    },
];

const rows = computed(() => quote.value?.lines);
</script>
